import { Icon } from '@iconify-icon/react';
import Link from 'next/link';

interface DocsArticleCardProps {
  title: string;
  description: string;
  href: string;
  icon?: string;
  gradient?: string;
  featured?: boolean;
  readingTime?: string;
  category?: string;
}

const DocsArticleCard: React.FC<DocsArticleCardProps> = ({
  title,
  description,
  href,
  icon = 'tabler:file-text',
  gradient = 'from-gray-500 to-gray-600',
  featured = false,
  readingTime,
  category,
}) => {
  if (featured) {
    return (
      <Link href={href}>
        <article className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all hover:shadow-xl">
          <div className={`bg-gradient-to-br ${gradient} p-8`}>
            <div className="flex items-start justify-between">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                <Icon icon={icon} className="text-3xl text-white" />
              </div>
              <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white">
                Featured
              </span>
            </div>
            <div className="mt-6">
              {category && <span className="text-sm font-medium text-white/80">{category}</span>}
              <h3 className="mt-2 text-2xl font-bold text-white">{title}</h3>
              <p className="mt-3 text-white/90">{description}</p>
            </div>
            <div className="mt-6 flex items-center justify-between">
              {readingTime && (
                <span className="flex items-center gap-1 text-sm text-white/80">
                  <Icon icon="tabler:clock" className="text-base" />
                  {readingTime}
                </span>
              )}
              <span className="flex items-center gap-2 text-sm font-medium text-white transition-all group-hover:gap-3">
                Read article
                <Icon icon="tabler:arrow-right" className="text-lg" />
              </span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link href={href}>
      <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-lg">
        <div className="flex items-start gap-4">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${gradient}`}>
            <Icon icon={icon} className="text-lg text-white" />
          </div>
          <div className="flex-1">
            {category && (
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {category}
              </span>
            )}
            <h3 className="mt-1 font-semibold text-gray-900 transition-colors group-hover:text-primary-600">
              {title}
            </h3>
          </div>
        </div>
        <p className="mt-3 line-clamp-2 flex-1 text-sm text-gray-600">{description}</p>
        <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
          {readingTime && (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Icon icon="tabler:clock" className="text-sm" />
              {readingTime}
            </span>
          )}
          <span className="flex items-center gap-1 text-sm font-medium text-primary-600">
            Read more
            <Icon icon="tabler:chevron-right" className="text-base" />
          </span>
        </div>
      </article>
    </Link>
  );
};

export default DocsArticleCard;
